import { UIElement } from '../ui-element.js';
import { Labels } from '../../global/labels.js';
import { getChildrenForSlot } from '../../global/ui-helpers.js';
import { UIIcon } from '../icon/ui-icon.js';
import styles from './ui-message.css';
import { element } from '../../global/template-literals.js';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIMessage
 * @element ui-message
 * @classdesc Represents a class for <code>ui-message</code> element.
 * Render alert message with a type.
 * @property {("default" | "light")} layout {@attr layout} Layout for message.
 * @property {AlertMessageType} type {@attr type}
 * Type of the message.
 *  {@desc info}
 *  {@desc warning}
 *  {@desc success}
 *  {@desc error}
 *  {@desc urgent}
 * @property {boolean} [closable=false] {@attr closable} add close button for 'light' layout.
 * @property {boolean} [persistent=false] {@attr persistent} persistence of message.
 * @property {boolean} [multiline=false] {@attr multiline} keeps the action button(s) on separate line.
 * @property {HTMLDivElement} content Shortcut to content element of the message.
 * @property {HTMLButtonElement} closeAction Shortcut to the button to close message.
 * @slot {@name action} Action element(s) of the message.
 * @example
 * <ui-message type="info">Info message</ui-message>
 * @example
 * <ui-message type="warning">Warning message</ui-message>
 * @example
 * <ui-message type="error">Error message</ui-message>
 * @example
 * <ui-message type="info" multiline>
 *   <p>A list inside info message</p>
 *   <ul class="regular-list -turquoise">
 *     <li>At vero eos et accusamus et iusto odio dignissimo</li>
 *     <li>Sed ut perspiciatis unde omnis iste natus error sit voluptatem</li>
 *   </ul>
 *   <ui-chip label="Activate" layout="button"></ui-chip>
 *   <ui-chip label="Close" layout="button"></ui-chip>
 * </ui-message>
 */
class UIMessage extends UIElement {
    /**
     * Layouts list.
     * @type {{DEFAULT: 'default', LIGHT: 'light'}}
     */
    static get layouts() {
        return {
            DEFAULT: 'default',
            LIGHT: 'light',
        };
    }

    /**
     * @type {IProps}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                layout: { type: String, default: UIMessage.layouts.DEFAULT },
                type: String,
                closable: Boolean,
                persistent: Boolean,
                multiLine: Boolean,
            },
            children: {
                content: '.ui-message__content',
                closeAction: '.ui-message__close-action',
            },
        };
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels() {
        return Labels.attach('ui-message', {
            close: 'Close',
        });
    }

    /**
     * Returns the icon glyph for the message.
     * @returns {string}
     */
    get iconGlyph() {
        switch (this.type) {
            case 'success':
                return 'redesign-alert-success';
            case 'info':
                return 'redesign-alert-info';
            case 'warning':
            case 'light-warning':
                return 'redesign-alert-warning';
            case 'error':
            case 'light-error':
                return 'redesign-alert-error';
            case 'urgent':
                return 'redesign-triangle-exclamation';
            default:
                console.warn(`Wrong message type provided - ${this.type}`);
                return '';
        }
    }

    /**
     * Checks if the node is empty.
     * @returns {boolean}
     */
    isEmpty() {
        return this.content.innerHTML.trim() === '';
    }

    /**
     * Checks if the message layout is 'light'
     * @returns {boolean}
     */
    isLightLayout() {
        return this.layout === UIMessage.layouts.LIGHT;
    }

    /**
     * Sets content to message and shows the message.
     * @param {string} message
     */
    showMessage(message) {
        this.content.innerHTML = message;
        this.show();
    }

    /**
     * Clears the message element.
     */
    clear() {
        if (this.persistent) {
            return;
        }
        this.content.innerHTML = '';
        this.hide();
        this.parentElement.removeChild(this);
    }

    /**
     * Handle closing action for message
     */
    handleCloseAction() {
        this.clear();
    }

    /**
     * @returns {string}
     */
    resolveRole() {
        return (
            this.role ||
            (['error', 'urgent', 'success'].includes(this.type)
                ? 'alert'
                : 'status')
        );
    }

    /**
     * @inheritDoc
     */
    render() {
        this.layout = this.layout || UIMessage.layouts.DEFAULT;
        this.type = this.type || (this.isLightLayout() ? 'warning' : 'info');
        const actionSlotElements = getChildrenForSlot(this, 'action');
        this.setAttribute('role', this.resolveRole());

        if (actionSlotElements.length > 0 && this.closable) {
            this.removeAttribute('closable');
        }

        const contentElements = this.detachChildNodes();
        const actionElement =
            actionSlotElements.length > 0
                ? element`
            <div class="ui-message__action-buttons-wrapper">
              ${actionSlotElements}
            </div>
          `
                : this.closable
                  ? element`
            <div class="ui-message__close-button-wrapper">
              <button
                  type="button"
                  aria-label="${UIMessage.labels.close}"
                  class="button -destructive ui-message__close-action"
              >
                <ui-icon glyph="redesign-x-mark" color="${UIIcon.colors.DEFAULT}"></ui-icon>
              </button>
            </div>
          `
                  : '';

        this.append(
            element`
            <div class="ui-message__icon-wrapper">
              <ui-icon
                  color="${!this.isLightLayout() ? UIIcon.colors.WHITE : null}"
                  glyph="${this.iconGlyph}"
                  class="ui-message__icon"
              ></ui-icon>      
          </div>`,
            element`<div class="ui-message__content">${contentElements}</div>`,
            actionElement
        );
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.handleCloseAction = this.handleCloseAction.bind(this);
        this.closeAction?.addEventListener('click', this.handleCloseAction);
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.closeAction?.removeEventListener('click', this.handleCloseAction);
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.closeAction?.addEventListener('click', this.handleCloseAction);
    }
}

UIMessage.defineElement('ui-message', styles);
export { UIMessage };
